/**
 * Landing — página pública de apresentação (SaaS v4 freemium).
 * Renderizada na rota `/` para visitantes DESLOGADOS; logados vão direto
 * para /semanal (redirect no App.tsx). Tailwind puro, paleta do app.
 */
import { Link } from 'react-router-dom';

const FEATURES = [
  {
    icone: '📡',
    titulo: 'DJEN automático',
    texto:
      'As intimações da sua OAB são coletadas direto do Diário de Justiça Eletrônico Nacional, com triagem por IA: tipo de prazo, urgência e contagem em dias úteis — sem digitação.',
    selo: null,
  },
  {
    icone: '🗂️',
    titulo: 'Kanban semanal de prazos',
    texto:
      'Triagem → Em andamento → Pronto p/ protocolar → Concluído. Arraste cards, atribua responsáveis, acompanhe vencimentos com SLA visual e visão semanal ou mensal.',
    selo: null,
  },
  {
    icone: '🤖',
    titulo: 'Peças com IA',
    texto:
      'Mapa do caso, blocos da petição, refino conversacional, pesquisa de jurisprudência (Falcão) e petição final em papel timbrado — gerados pela conta Claude do seu escritório.',
    selo: 'PRO',
  },
];

const COMPARATIVO: { recurso: string; free: string; pro: string }[] = [
  { recurso: 'Coleta DJEN da sua OAB',            free: '✓', pro: '✓' },
  { recurso: 'Kanban semanal e mensal',           free: '✓', pro: '✓' },
  { recurso: 'Triagem automática por IA',         free: '✓', pro: '✓' },
  { recurso: 'Agenda de audiências',              free: '✓', pro: '✓' },
  { recurso: 'Mapa do caso + blocos da petição',  free: '—', pro: '✓' },
  { recurso: 'Refino conversacional com IA',      free: '—', pro: '✓' },
  { recurso: 'Jurisprudência (Falcão/JT)',        free: '—', pro: '✓' },
  { recurso: 'Petição final em papel timbrado',   free: '—', pro: '✓' },
];

export function Landing() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* ─── Topbar ─── */}
      <header className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center font-bold text-white">A</div>
          <span className="font-display font-bold text-lg tracking-tight">Agenda de Prazos · TRT-1</span>
        </div>
        <nav className="flex items-center gap-3">
          <Link to="/login" className="text-sm px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition">
            Entrar
          </Link>
          <Link to="/signup" className="text-sm px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold transition">
            Criar conta grátis
          </Link>
        </nav>
      </header>

      {/* ─── Hero ─── */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20 text-center">
        <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight max-w-3xl mx-auto">
          Os prazos do TRT-1, direto do DJEN,
          <span className="text-blue-400"> no seu kanban</span>.
        </h1>
        <p className="mt-5 text-lg text-slate-400 max-w-2xl mx-auto">
          Informe a OAB do seu escritório e receba as intimações já triadas por IA — tipo de prazo,
          urgência, vencimento em dias úteis. Organize tudo num kanban semanal feito para a advocacia trabalhista.
        </p>
        <div className="mt-8 flex items-center justify-center gap-4">
          <Link
            to="/signup"
            className="px-7 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-base shadow-lg shadow-blue-600/25 transition"
          >
            Criar conta grátis →
          </Link>
          <Link to="/login" className="px-7 py-3 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 font-medium transition">
            Já tenho conta
          </Link>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Grátis para monitorar prazos. Sem cartão de crédito.
        </p>
      </section>

      {/* ─── Features ─── */}
      <section className="max-w-6xl mx-auto px-6 pb-20 grid md:grid-cols-3 gap-6">
        {FEATURES.map((f) => (
          <div key={f.titulo} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 relative">
            {f.selo && (
              <span className="absolute top-4 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                ⭐ {f.selo}
              </span>
            )}
            <div className="text-3xl">{f.icone}</div>
            <h3 className="mt-3 font-display font-bold text-lg">{f.titulo}</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.texto}</p>
          </div>
        ))}
      </section>

      {/* ─── Free vs Pro ─── */}
      <section className="max-w-3xl mx-auto px-6 pb-20">
        <h2 className="font-display text-2xl font-bold text-center mb-8">Free vs Pro</h2>
        <div className="rounded-2xl border border-slate-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 text-slate-300">
                <th className="text-left px-5 py-3 font-semibold">Recurso</th>
                <th className="px-5 py-3 font-semibold w-24">Free</th>
                <th className="px-5 py-3 font-semibold w-24 text-amber-300">Pro</th>
              </tr>
            </thead>
            <tbody>
              {COMPARATIVO.map((l, i) => (
                <tr key={l.recurso} className={i % 2 ? 'bg-slate-900/40' : ''}>
                  <td className="px-5 py-2.5 text-slate-300">{l.recurso}</td>
                  <td className="px-5 py-2.5 text-center text-emerald-400">{l.free}</td>
                  <td className="px-5 py-2.5 text-center text-emerald-400">{l.pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-slate-500 text-center">
          O plano Pro usa a conta Claude do seu escritório para gerar as peças — consumo de IA por escritório,
          sob seu controle.
        </p>
      </section>

      {/* ─── CTA final ─── */}
      <section className="max-w-6xl mx-auto px-6 pb-24 text-center">
        <div className="rounded-3xl border border-blue-900/60 bg-gradient-to-b from-blue-950/60 to-slate-950 px-8 py-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Nenhum prazo perdido. Nunca mais.</h2>
          <p className="mt-3 text-slate-400">Cadastre a OAB do escritório e veja seus prazos aparecerem no kanban.</p>
          <Link
            to="/signup"
            className="mt-6 inline-block px-8 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-lg shadow-blue-600/25 transition"
          >
            Começar agora — é grátis
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-900 py-8 text-center text-xs text-slate-600">
        Agenda de Prazos TRT-1 · DJEN/Comunica (comunicaapi.pje.jus.br) · {new Date().getFullYear()}
        <br />
        <span className="text-slate-700">Conexão sem HTTPS no momento — não reutilize senhas de outros serviços.</span>
      </footer>
    </div>
  );
}
